import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material';
import { client, consoleLogInDev } from 'shared';
import download from 'shared/Download';
import { Card, Row, Select, PillButton, Stack, Text, Title } from 'library/ui';

const InvoiceDownload = () => {
    type stringKeyValuePair = { key: string; value: string };
    type numberKeyValuePair = { key: number; value: string };
    const invoiceTypeOptions = [
        { key: 'unpaid', value: 'Unpaid' },
        { key: 'weekly', value: 'Weekly' },
        { key: 'monthly', value: 'Monthly' },
        { key: 'yearly', value: 'Yearly' },
    ];
    const [statementOptions, setStatementOptions] = useState<stringKeyValuePair[]>([]);
    const [providerOrCenterLocationOptions, setProviderOrCenterLocationOptions] = useState<numberKeyValuePair[]>([]);
    const [providerOptions, setProviderOptions] = useState<numberKeyValuePair[]>([]);

    const [selectedStatement, setSelectedStatement] = useState('');
    const [selectedInvoiceType, setSelectedInvoiceType] = useState('weekly');
    const [selectedProviderOrCenterLocation, setSelectedProviderOrCenterLocation] = useState(-1);
    const [selectedProvider, setSelectedProvider] = useState(-1);

    const [downloading, setDownloading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        getProviderOrCenerLocationOptions();
    }, []);
    useEffect(() => {
        getProvidersForCenterLocation();
    }, [selectedProviderOrCenterLocation]);

    useEffect(() => {
        getStatementList();
    }, [selectedInvoiceType, selectedProviderOrCenterLocation, selectedProvider]);

    const getProvidersForCenterLocation = () => {
        if (selectedProviderOrCenterLocation === -1) {
            setProviderOptions([{ key: -1, value: 'All' }]);
            setSelectedProvider(-1);
            return;
        }

        client('payment/api/invoice/providers-for-location/?location_id=' + selectedProviderOrCenterLocation)
            .then((response: numberKeyValuePair[]) => {
                response = [{ key: -1, value: 'All Providers' }, ...response];
                setProviderOptions(response);
                setSelectedProvider(-1);
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    };

    const getProviderOrCenerLocationOptions = () => {
        client('payment/api/invoice/provider-or-center-locations-list/')
            .then((response: numberKeyValuePair[]) => {
                response = [{ key: -1, value: 'All Locations' }, ...response];
                setProviderOrCenterLocationOptions(response);
                setSelectedProviderOrCenterLocation(response[0]['key']);
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    };

    const getStatementList = () => {
        client(`payment/api/invoice/statement-list/?style=${selectedInvoiceType}`)
            .then((response: string[]) => {
                setStatementOptions(
                    response.map((x) => {
                        return {
                            key: x,
                            value: x,
                        };
                    }),
                );
                setSelectedStatement(response[0]);
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    };

    const onDownloadClick = () => {
        setDownloading(true);
        setErrorMessage('');
        download(
            `payment/api/invoice/statement/?week=${encodeURIComponent(
                selectedStatement,
            )}&style=${selectedInvoiceType}&paid_user=${selectedProvider}&bus_location=${selectedProviderOrCenterLocation}`,
        )
            .catch((error) => {
                if (error.message == 'Not found') {
                    setErrorMessage('No invoices found');
                } else {
                    setErrorMessage('There was an error generating your invoice');
                }
            })
            .finally(() => {
                setDownloading(false);
            });
    };

    return (
        <Card padding="lg" fullHeight>
            <Stack gap="md" fullHeight>
                <Stack gap="md" grow>
                    <Stack gap="none">
                        <Title size="h2">Download Invoice</Title>
                        <Text size="sm" tone="secondary">
                            Generate a PDF for any date range or location
                        </Text>
                    </Stack>
                    <Row gap="md" align="start">
                        <SelectColumn>
                            <Select
                                fullWidth
                                value={selectedInvoiceType}
                                options={invoiceTypeOptions}
                                onChange={(value) => setSelectedInvoiceType(value)}
                            />
                        </SelectColumn>
                        <SelectColumn>
                            <Select
                                fullWidth
                                value={selectedProviderOrCenterLocation}
                                options={providerOrCenterLocationOptions}
                                onChange={(value) => setSelectedProviderOrCenterLocation(Number(value))}
                            />
                        </SelectColumn>
                    </Row>
                    <Row gap="md" align="start">
                        <SelectColumn>
                            <Select
                                fullWidth
                                value={selectedProvider}
                                options={providerOptions}
                                onChange={(value) => setSelectedProvider(Number(value))}
                            />
                        </SelectColumn>
                        <SelectColumnWide>
                            <Select
                                fullWidth
                                value={selectedStatement}
                                options={statementOptions}
                                onChange={(value) => setSelectedStatement(value)}
                            />
                        </SelectColumnWide>
                    </Row>
                </Stack>
                <Stack gap="sm">
                    <Row justify="end">
                        <PillButton disabled={!selectedStatement || downloading} onClick={() => onDownloadClick()}>
                            {downloading ? 'Downloading...' : 'Download'}
                        </PillButton>
                    </Row>
                    {errorMessage && (
                        <Text size="sm" tone="error">
                            {errorMessage}
                        </Text>
                    )}
                </Stack>
            </Stack>
        </Card>
    );
};

export default InvoiceDownload;

const SelectColumn = styled('div')({
    flex: 1,
    minWidth: 0,
});

const SelectColumnWide = styled('div')({
    flex: 2,
    minWidth: 0,
});
